// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Pure CSV parsing for the CSV import dialog, extracted from the component.
 * Three side-effect-free steps, now individually unit-testable:
 *
 *  • `detectSeparator` — comma vs semicolon, by whichever is more frequent in
 *    the header line (European exports often use ';').
 *  • `parseCsvLine` — a quote-aware field splitter: honours quoted fields that
 *    contain the separator and `""` escaped quotes.
 *  • `parseCsvToJson` — header row + data rows → an array of `{header: value}`
 *    objects, skipping rows whose field count doesn't match the header (logged).
 *
 * Behaviour is preserved verbatim from the former inline parser. No money /
 * reseller here — wall-neutral.
 */

/** Pick the delimiter for a line: ';' when semicolons outnumber commas, else ','. */
export function detectSeparator(line: string): string {
  const commaCount = (line.match(/,/g) || []).length
  const semicolonCount = (line.match(/;/g) || []).length
  return semicolonCount > commaCount ? ";" : ","
}

/**
 * Split one CSV line into trimmed fields, respecting quoted sections (a `sep`
 * inside quotes is literal) and `""` as an escaped quote.
 */
export function parseCsvLine(line: string, sep: string): string[] {
  const result: string[] = []
  let current = ""
  let inQuotes = false
  let i = 0

  while (i < line.length) {
    const char = line[i]

    if (char === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        // Handle escaped quotes
        current += '"'
        i += 2
      } else {
        // Toggle quote state
        inQuotes = !inQuotes
        i++
      }
    } else if (char === sep && !inQuotes) {
      // End of field
      result.push(current.trim())
      current = ""
      i++
    } else {
      current += char
      i++
    }
  }

  // Add the last field
  result.push(current.trim())
  return result
}

/**
 * Parse CSV text into row objects keyed by the header row. Blank lines are
 * dropped; fewer than 2 non-blank lines yields `[]`; rows whose field count
 * doesn't match the header are skipped (and logged), preserving the original
 * lenient behaviour.
 */
export function parseCsvToJson(csvText: string): Record<string, string>[] {
  const lines = csvText.split("\n").filter((line) => line.trim())
  if (lines.length < 2) return []

  const separator = detectSeparator(lines[0]!)

  const headers = parseCsvLine(lines[0]!, separator).map((h) => h.replace(/"/g, ""))
  const data: Record<string, string>[] = []

  for (let i = 1; i < lines.length; i++) {
    const values = parseCsvLine(lines[i]!, separator).map((v) => v.replace(/"/g, ""))

    if (values.length === headers.length) {
      const row: Record<string, string> = {}
      headers.forEach((header, index) => {
        row[header] = values[index] || ""
      })
      data.push(row)
    } else {
      console.warn(`Row ${i} field count mismatch: expected ${headers.length}, got ${values.length}`)
    }
  }

  return data
}
